/**
 * Investment Advisor Page
 *
 * Dedicated page for AI-powered investment advice with:
 * - Full portfolio context
 * - Chat interface with history
 * - Visual portfolio metrics
 * - Suggested questions based on portfolio state
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../components/Icons';
import { SafeHTML } from '../components/common/SafeHTML';
import { PortfolioContextService, type PortfolioContext } from '../services/portfolioContextService';
import { CalculatorFactory } from '../services/calculator';
import { StorageService } from '../services/storage';
import { PortfolioManager } from '../services/portfolioManager';
import { usePortfolio } from '../hooks/usePortfolio';
import { usePositions } from '../hooks/usePositions';
import { useCoinPrices } from '../hooks/queries/useCoinGecko';
import { useAdvisorConversations, type AdvisorConversation, type AdvisorMessage, type HistorySummary } from '../hooks/useAdvisorConversations';
import type { Token, Position, FIFOResults, CalculationMethod, Prices } from '../types';

// Chat message interface
interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

// Conversation interface for history
interface Conversation {
  id: string;
  title: string;
  messages: ChatMessage[];
  createdAt: Date;
  updatedAt: Date;
}

// Max conversations to show in history
const MAX_CONVERSATIONS_DISPLAY = 20;

// Get browser language
function getBrowserLanguage(): 'fr' | 'en' {
  const lang = navigator.language || 'en';
  return lang.toLowerCase().startsWith('fr') ? 'fr' : 'en';
}

// Generate unique session ID
function generateId(): string {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

// Suggested questions based on portfolio state
function getSuggestedQuestions(context: PortfolioContext, language: 'fr' | 'en'): string[] {
  const questions: string[] = [];
  const { summary, tokens, risk } = context;

  if (language === 'fr') {
    // Basic questions
    questions.push('Fais-moi un résumé de mon portefeuille');

    // Performance-based
    if (summary.worstPerformer) {
      questions.push(`Pourquoi ${summary.worstPerformer.symbol} performe mal ?`);
    }
    if (summary.bestPerformer) {
      questions.push(`Dois-je prendre des profits sur ${summary.bestPerformer.symbol} ?`);
    }

    // Risk-based
    if (risk.concentrationRisk === 'high') {
      questions.push('Mon portefeuille est-il trop concentré ?');
    }
    if (risk.diversificationScore < 50) {
      questions.push('Comment diversifier mon portefeuille ?');
    }

    // Scenario questions
    questions.push('Si BTC monte de 30%, quel sera l\'impact ?');
    questions.push('Quels tokens devrais-je renforcer ?');

    // Loss recovery
    if (summary.losingTokens > 0) {
      questions.push('Comment récupérer mes pertes ?');
    }

    // Target prices
    const tokensWithTarget = tokens.filter(t => t.hasTargetPrice);
    if (tokensWithTarget.length > 0) {
      questions.push('Quand atteindrai-je mes objectifs de prix ?');
    }
  } else {
    // English version
    questions.push('Give me a summary of my portfolio');

    if (summary.worstPerformer) {
      questions.push(`Why is ${summary.worstPerformer.symbol} performing poorly?`);
    }
    if (summary.bestPerformer) {
      questions.push(`Should I take profits on ${summary.bestPerformer.symbol}?`);
    }

    if (risk.concentrationRisk === 'high') {
      questions.push('Is my portfolio too concentrated?');
    }
    if (risk.diversificationScore < 50) {
      questions.push('How can I diversify my portfolio?');
    }

    questions.push('If BTC rises 30%, what will be the impact?');
    questions.push('Which tokens should I increase?');

    if (summary.losingTokens > 0) {
      questions.push('How can I recover my losses?');
    }

    const tokensWithTarget = tokens.filter(t => t.hasTargetPrice);
    if (tokensWithTarget.length > 0) {
      questions.push('When will I reach my price targets?');
    }
  }

  return questions.slice(0, 8); // Max 8 suggestions
}

// Format message content with markdown-like styling
function formatContent(content: string): string {
  // Bold text
  let formatted = content.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  // Line breaks
  formatted = formatted.replace(/\n/g, '<br>');
  // Bullet points
  formatted = formatted.replace(/^- /gm, '• ');
  return formatted;
}

// Metric Card Component
function MetricCard({
  icon: Icon,
  label,
  value,
  subValue,
  color = 'default',
}: {
  icon: React.ComponentType<{ size?: number }>;
  label: string;
  value: string;
  subValue?: string;
  color?: 'default' | 'green' | 'red' | 'yellow';
}) {
  const colorStyles = {
    default: { bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.1)', text: 'var(--text-primary)' },
    green: { bg: 'rgba(16, 185, 129, 0.1)', border: 'rgba(16, 185, 129, 0.3)', text: '#10b981' },
    red: { bg: 'rgba(239, 68, 68, 0.1)', border: 'rgba(239, 68, 68, 0.3)', text: '#ef4444' },
    yellow: { bg: 'rgba(245, 158, 11, 0.1)', border: 'rgba(245, 158, 11, 0.3)', text: '#f59e0b' },
  };

  const style = colorStyles[color];

  return (
    <div
      style={{
        padding: '16px',
        borderRadius: '12px',
        background: style.bg,
        border: `1px solid ${style.border}`,
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}
    >
      <div
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '10px',
          background: style.border,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={20} />
      </div>
      <div>
        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
          {label}
        </div>
        <div style={{ fontSize: '18px', fontWeight: 600, color: style.text }}>
          {value}
        </div>
        {subValue && (
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
            {subValue}
          </div>
        )}
      </div>
    </div>
  );
}

// Token Mini Card
function TokenMiniCard({ token }: { token: PortfolioContext['tokens'][0] }) {
  const isPositive = token.pl >= 0;

  return (
    <div
      style={{
        padding: '12px',
        borderRadius: '8px',
        background: 'var(--card-bg)',
        border: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'var(--hover-bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            fontWeight: 600,
          }}
        >
          {token.symbol.slice(0, 2)}
        </div>
        <div>
          <div style={{ fontWeight: 500, fontSize: '14px' }}>{token.symbol}</div>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
            {token.allocation.toFixed(1)}%
          </div>
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: '14px', fontWeight: 500 }}>
          ${token.currentValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </div>
        <div
          style={{
            fontSize: '12px',
            color: isPositive ? '#10b981' : '#ef4444',
          }}
        >
          {isPositive ? '+' : ''}{token.plPercent.toFixed(1)}%
        </div>
      </div>
    </div>
  );
}

export function Advisor() {
  const { t } = useTranslation();
  const language = getBrowserLanguage();

  // 🔄 HYBRID MODE: Use Portfolio & Positions Hooks (localStorage OR Supabase)
  const portfolioHook = usePortfolio();
  const positionsHook = usePositions(portfolioHook.activePortfolio?.id);

  // Portfolio data state
  const [tokens, setTokens] = useState<Token[]>([]);
  const [loading, setLoading] = useState(true);

  // 🔄 HYBRID MODE: Sync hook data with local state
  useEffect(() => {
    // Update loading state
    const isInitialLoading = (portfolioHook.loading || positionsHook.loading) && tokens.length === 0;
    setLoading(isInitialLoading);

    // Combine tokens + positions into Token format
    if (portfolioHook.tokens.length > 0 || positionsHook.positions.length > 0) {
      // Group positions by token
      const positionsByToken = new Map<string, Position[]>();

      positionsHook.positions.forEach((hybridPos) => {
        const tokenId = (hybridPos as any).coin_id || ('token_id' in hybridPos ? hybridPos.token_id : hybridPos.tokenId);

        const legacyPosition: Position = {
          id: hybridPos.id,
          type: hybridPos.type,
          date: hybridPos.date,
          quantity: typeof hybridPos.quantity === 'string' ? parseFloat(hybridPos.quantity) : hybridPos.quantity,
          price: typeof hybridPos.price === 'string' ? parseFloat(hybridPos.price) : hybridPos.price,
          note: hybridPos.note || undefined,
          is_swap: (hybridPos as any).is_swap,
          swap_chain_id: (hybridPos as any).swap_chain_id,
          original_cost_basis: (hybridPos as any).original_cost_basis,
        };

        const existing = positionsByToken.get(tokenId) || [];
        positionsByToken.set(tokenId, [...existing, legacyPosition]);
      });

      // Convert hybrid tokens to Token format
      let legacyTokens: Token[] = portfolioHook.tokens.map((hybridToken) => {
        const tokenId = 'coin_id' in hybridToken ? hybridToken.coin_id : hybridToken.id;
        const displayName = 'symbol' in hybridToken ? hybridToken.symbol : 'name' in hybridToken ? hybridToken.name : tokenId;

        return {
          id: tokenId,
          displayName: (displayName || tokenId || 'UNKNOWN').toUpperCase(),
          positions: positionsByToken.get(tokenId) || [],
          note: 'notes' in hybridToken ? hybridToken.notes : undefined,
          tags: [],
          targetPrice: hybridToken.targetPrice || hybridToken.target_price || undefined,
        };
      });

      // Fallback: If tokens empty but we have positions, read from PortfolioManager
      if (legacyTokens.length === 0 && positionsHook.positions.length > 0 && portfolioHook.activePortfolio) {
        const pmPortfolio = PortfolioManager.getPortfolio(portfolioHook.activePortfolio.id);
        if (pmPortfolio && pmPortfolio.tokens && pmPortfolio.tokens.length > 0) {
          legacyTokens = pmPortfolio.tokens.map(token => ({
            id: token.id,
            displayName: (token.displayName || token.id || 'UNKNOWN').toUpperCase(),
            positions: positionsByToken.get(token.id) || token.positions || [],
            note: token.note,
            tags: token.tags || [],
            targetPrice: token.targetPrice,
          }));
        }
      }

      // Don't set empty tokens if we previously had tokens
      if (legacyTokens.length > 0 || tokens.length === 0) {
        setTokens(legacyTokens);
      }
    }
  }, [portfolioHook.activePortfolio, portfolioHook.tokens, portfolioHook.loading, positionsHook.positions, positionsHook.loading]);

  // Fetch prices for tokens
  const tokenIds = useMemo(() => tokens.map(t => t.id), [tokens]);
  const { data: prices = {} as Prices } = useCoinPrices(tokenIds, {
    enabled: tokenIds.length > 0,
  });

  // Calculate FIFO results
  const calculationMethod = StorageService.get<CalculationMethod>('calculation_method', 'FIFO');
  const fifoResults: FIFOResults = useMemo(() => {
    const results: FIFOResults = {};
    tokens.forEach(token => {
      results[token.id] = CalculatorFactory.calculate(token.positions, calculationMethod);
    });
    return results;
  }, [tokens, calculationMethod]);

  // Build portfolio context
  const portfolioContext = useMemo(() => {
    return PortfolioContextService.buildContext(tokens, prices, fifoResults, calculationMethod);
  }, [tokens, prices, fifoResults, calculationMethod]);

  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 🔄 SUPABASE: Use advisor conversations hook for database storage
  const {
    conversations: dbConversations,
    loading: conversationsLoading,
    createConversation,
    updateConversation,
    deleteConversation: deleteDbConversation,
    fetchMessages,
    addMessage,
    getHistorySummary,
    isAuthenticated,
  } = useAdvisorConversations();

  // Convert DB conversations to local format for display
  const conversations = useMemo(() => {
    return dbConversations.map(c => ({
      id: c.id,
      title: c.title,
      messages: [], // Messages loaded on demand
      createdAt: new Date(c.created_at),
      updatedAt: new Date(c.updated_at),
    }));
  }, [dbConversations]);

  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [historySummary, setHistorySummary] = useState<HistorySummary | null>(null);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const sessionIdRef = useRef<string>(generateId());

  // Suggested questions
  const suggestedQuestions = useMemo(() => {
    return getSuggestedQuestions(portfolioContext, language);
  }, [portfolioContext, language]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // 🔄 SUPABASE: Fetch history summary for AI context
  useEffect(() => {
    if (isAuthenticated) {
      getHistorySummary().then(summary => {
        if (summary) setHistorySummary(summary);
      });
    }
  }, [isAuthenticated, getHistorySummary]);

  // Welcome message
  const getWelcomeMessage = useCallback((): string => {
    const { summary } = portfolioContext;
    if (language === 'fr') {
      return `Bienvenue dans votre espace Conseiller Investissement !

J'ai accès à votre portefeuille complet:
• **${summary.tokenCount} tokens** pour une valeur de **$${summary.totalValue.toLocaleString()}**
• P/L global: **${summary.totalPLPercent > 0 ? '+' : ''}${summary.totalPLPercent.toFixed(2)}%**
• ${summary.profitableTokens} tokens en profit, ${summary.losingTokens} en perte

Posez-moi vos questions sur vos investissements, je peux analyser, calculer des scénarios, et vous conseiller.

⚠️ *Ceci n'est pas un conseil financier réglementé. Faites toujours vos propres recherches.*`;
    } else {
      return `Welcome to your Investment Advisor space!

I have access to your complete portfolio:
• **${summary.tokenCount} tokens** worth **$${summary.totalValue.toLocaleString()}**
• Overall P/L: **${summary.totalPLPercent > 0 ? '+' : ''}${summary.totalPLPercent.toFixed(2)}%**
• ${summary.profitableTokens} tokens in profit, ${summary.losingTokens} at a loss

Ask me your investment questions, I can analyze, calculate scenarios, and advise you.

⚠️ *This is not regulated financial advice. Always do your own research.*`;
    }
  }, [portfolioContext, language]);

  // Initialize or load conversation
  useEffect(() => {
    if (messages.length === 0 && !activeConversationId) {
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: getWelcomeMessage(),
        timestamp: new Date(),
      }]);
    }
  }, [messages.length, activeConversationId, getWelcomeMessage]);

  // Send message
  const sendMessage = useCallback(async (messageText?: string) => {
    const text = messageText || inputValue.trim();
    if (!text || isLoading) return;

    setError(null);
    setInputValue('');

    const userMessage: ChatMessage = {
      id: generateId(),
      role: 'user',
      content: text,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    // 🔄 SUPABASE: Create or get conversation ID
    let conversationId = activeConversationId;
    if (!conversationId) {
      // Create new conversation with portfolio snapshot
      const portfolioSnapshot = {
        total_value: portfolioContext.summary.totalValue,
        token_count: portfolioContext.summary.tokenCount,
        top_tokens: portfolioContext.tokens.slice(0, 5).map(t => ({
          symbol: t.symbol,
          value: t.currentValue,
          pl_percent: t.plPercent,
        })),
        pl_percent: portfolioContext.summary.totalPLPercent,
        risk_score: portfolioContext.risk.diversificationScore,
      };

      const newConversation = await createConversation({
        title: text.slice(0, 50) + (text.length > 50 ? '...' : ''),
        portfolioSnapshot,
        source: 'page',
        language,
      });

      if (newConversation) {
        conversationId = newConversation.id;
        setActiveConversationId(conversationId);
      }
    }

    // Save user message to database
    if (conversationId) {
      await addMessage({
        conversationId,
        role: 'user',
        content: text,
      });
    }

    try {
      const response = await fetch('/api/advisor/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          history: messages.filter(m => m.id !== 'welcome').map(m => ({ role: m.role, content: m.content })),
          portfolioContext,
          language,
          sessionId: sessionIdRef.current,
          // 🔄 SUPABASE: Include history summary for better AI context
          historySummary: historySummary || undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Communication error');
      }

      const assistantMessage: ChatMessage = {
        id: generateId(),
        role: 'assistant',
        content: data.message,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMessage]);

      // 🔄 SUPABASE: Save assistant message to database
      if (conversationId) {
        // Detect if message contains recommendation
        const recommendationKeywords = ['recommend', 'suggest', 'consider', 'acheter', 'vendre', 'conseille', 'recommande'];
        const containsRecommendation = recommendationKeywords.some(kw =>
          data.message.toLowerCase().includes(kw)
        );

        // Detect recommendation type
        let recommendationType: string | undefined;
        if (data.message.toLowerCase().includes('buy') || data.message.toLowerCase().includes('acheter')) {
          recommendationType = 'buy';
        } else if (data.message.toLowerCase().includes('sell') || data.message.toLowerCase().includes('vendre')) {
          recommendationType = 'sell';
        } else if (data.message.toLowerCase().includes('hold') || data.message.toLowerCase().includes('conserver')) {
          recommendationType = 'hold';
        } else if (data.message.toLowerCase().includes('diversif')) {
          recommendationType = 'diversify';
        } else if (data.message.toLowerCase().includes('rebalance') || data.message.toLowerCase().includes('rééquilibr')) {
          recommendationType = 'rebalance';
        }

        // Extract mentioned tokens
        const tokenSymbols = portfolioContext.tokens.map(t => t.symbol.toUpperCase());
        const tokensMentioned = tokenSymbols.filter(symbol =>
          data.message.toUpperCase().includes(symbol)
        );

        await addMessage({
          conversationId,
          role: 'assistant',
          content: data.message,
          metadata: {
            model_used: data.model || 'unknown',
            fallback_used: data.fallbackUsed || false,
          },
          containsRecommendation,
          recommendationType,
          tokensMentioned: tokensMentioned.length > 0 ? tokensMentioned : undefined,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, isLoading, messages, portfolioContext, language, activeConversationId, createConversation, addMessage, historySummary]);

  // Handle Enter key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // 🔄 SUPABASE: Load conversation from history (fetch messages from DB)
  const loadConversation = async (conversation: Conversation) => {
    setActiveConversationId(conversation.id);

    // Fetch messages from database
    const dbMessages = await fetchMessages(conversation.id);

    // Convert to ChatMessage format
    const loadedMessages: ChatMessage[] = dbMessages.map(m => ({
      id: m.id,
      role: m.role as 'user' | 'assistant',
      content: m.content,
      timestamp: new Date(m.created_at),
    }));

    setMessages([
      {
        id: 'welcome',
        role: 'assistant',
        content: getWelcomeMessage(),
        timestamp: new Date(),
      },
      ...loadedMessages,
    ]);
    setShowHistory(false);
    setShowMobileSidebar(false);
  };

  // Start new conversation
  const startNewConversation = () => {
    setActiveConversationId(null);
    setMessages([{
      id: 'welcome',
      role: 'assistant',
      content: getWelcomeMessage(),
      timestamp: new Date(),
    }]);
    sessionIdRef.current = generateId();
  };

  // 🔄 SUPABASE: Delete conversation from database
  const deleteConversation = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await deleteDbConversation(id);
    if (activeConversationId === id) {
      startNewConversation();
    }
  };

  // No portfolio data
  if (tokens.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          padding: '40px',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'rgba(16, 185, 129, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '24px',
          }}
        >
          <Icons.TrendingUp size={40} color="#10b981" />
        </div>
        <h2 style={{ marginBottom: '12px', color: 'var(--text-primary)' }}>
          {language === 'fr' ? 'Conseiller Investissement IA' : 'AI Investment Advisor'}
        </h2>
        <p style={{ color: 'var(--text-secondary)', maxWidth: '400px', marginBottom: '24px' }}>
          {language === 'fr'
            ? 'Ajoutez des tokens à votre portefeuille pour recevoir des conseils personnalisés.'
            : 'Add tokens to your portfolio to receive personalized advice.'}
        </p>
        <a
          href="/portfolio"
          style={{
            padding: '12px 24px',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            textDecoration: 'none',
            fontWeight: 500,
          }}
        >
          {language === 'fr' ? 'Aller au Portfolio' : 'Go to Portfolio'}
        </a>
      </div>
    );
  }

  // Sidebar content component (reused for desktop and mobile)
  const SidebarContent = () => (
    <>
      {/* New Chat Button */}
      <button
        onClick={() => {
          startNewConversation();
          setShowMobileSidebar(false);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: '12px',
          borderRadius: '12px',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          background: 'rgba(16, 185, 129, 0.1)',
          color: '#10b981',
          cursor: 'pointer',
          fontWeight: 500,
          transition: 'all 0.2s',
          width: '100%',
        }}
      >
        <Icons.Plus size={18} />
        {language === 'fr' ? 'Nouvelle conversation' : 'New conversation'}
      </button>

      {/* Portfolio Metrics */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <MetricCard
          icon={Icons.Briefcase}
          label={language === 'fr' ? 'Valeur totale' : 'Total Value'}
          value={`$${portfolioContext.summary.totalValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
          subValue={`${portfolioContext.summary.tokenCount} tokens`}
        />
        <MetricCard
          icon={Icons.TrendingUp}
          label="P/L"
          value={`${portfolioContext.summary.totalPLPercent > 0 ? '+' : ''}${portfolioContext.summary.totalPLPercent.toFixed(2)}%`}
          subValue={`$${portfolioContext.summary.totalPL.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
          color={portfolioContext.summary.totalPL >= 0 ? 'green' : 'red'}
        />
        <MetricCard
          icon={Icons.Shield}
          label={language === 'fr' ? 'Diversification' : 'Diversification'}
          value={`${portfolioContext.risk.diversificationScore}/100`}
          subValue={`${language === 'fr' ? 'Risque' : 'Risk'}: ${portfolioContext.risk.concentrationRisk}`}
          color={portfolioContext.risk.concentrationRisk === 'high' ? 'red' : portfolioContext.risk.concentrationRisk === 'medium' ? 'yellow' : 'green'}
        />
      </div>

      {/* Top Tokens */}
      <div>
        <h3 style={{ marginBottom: '12px', color: 'var(--text-secondary)', fontSize: '12px', textTransform: 'uppercase' }}>
          {language === 'fr' ? 'Top Tokens' : 'Top Tokens'}
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {portfolioContext.tokens.slice(0, 5).map(token => (
            <TokenMiniCard key={token.id} token={token} />
          ))}
        </div>
      </div>

      {/* Conversation History */}
      {conversations.length > 0 && (
        <div>
          <h4 style={{ marginBottom: '12px', color: 'var(--text-secondary)', fontSize: '12px', textTransform: 'uppercase' }}>
            {language === 'fr' ? 'Historique' : 'History'}
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '200px', overflowY: 'auto' }}>
            {conversations.slice(0, 10).map(conv => (
              <div
                key={conv.id}
                onClick={() => {
                  loadConversation(conv);
                  setShowMobileSidebar(false);
                }}
                style={{
                  padding: '10px 12px',
                  borderRadius: '8px',
                  background: activeConversationId === conv.id ? 'rgba(16, 185, 129, 0.1)' : 'var(--card-bg)',
                  border: `1px solid ${activeConversationId === conv.id ? 'rgba(16, 185, 129, 0.3)' : 'var(--border)'}`,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                }}
              >
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  <div style={{ fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {conv.title}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                    {new Date(conv.updatedAt).toLocaleDateString()}
                  </div>
                </div>
                <button
                  onClick={(e) => deleteConversation(conv.id, e)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '4px',
                    cursor: 'pointer',
                    opacity: 0.5,
                  }}
                >
                  <Icons.X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );

  return (
    <div style={{
      display: 'flex',
      minHeight: 'calc(100vh - 60px)',
      gap: isMobile ? '0' : '24px',
      padding: isMobile ? '0' : '24px',
    }}>
      {/* Mobile Sidebar Overlay */}
      {isMobile && showMobileSidebar && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setShowMobileSidebar(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 998,
            }}
          />
          {/* Sidebar Panel */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              bottom: 0,
              width: '300px',
              maxWidth: '85vw',
              background: 'var(--bg-primary, #1a1a2e)',
              zIndex: 999,
              padding: '16px',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            {/* Close button */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>
                {language === 'fr' ? 'Menu' : 'Menu'}
              </h3>
              <button
                onClick={() => setShowMobileSidebar(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '8px',
                  cursor: 'pointer',
                  color: 'var(--text-primary)',
                }}
              >
                <Icons.X size={24} />
              </button>
            </div>
            <SidebarContent />
          </div>
        </>
      )}

      {/* Desktop Left Sidebar */}
      {!isMobile && (
        <div
          style={{
            width: '300px',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <SidebarContent />
        </div>
      )}

      {/* Main Chat Area */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--card-bg)',
          borderRadius: isMobile ? '0' : '16px',
          border: isMobile ? 'none' : '1px solid var(--border)',
          overflow: 'hidden',
          minHeight: isMobile ? '100vh' : 'auto',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: isMobile ? '12px 16px' : '16px 24px',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          {/* Mobile Menu Button */}
          {isMobile && (
            <button
              onClick={() => setShowMobileSidebar(true)}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '8px',
                padding: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icons.Menu size={22} color="white" />
            </button>
          )}
          <div
            style={{
              width: isMobile ? '36px' : '48px',
              height: isMobile ? '36px' : '48px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icons.TrendingUp size={isMobile ? 20 : 26} color="white" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{
              color: 'white',
              fontSize: isMobile ? '16px' : '20px',
              fontWeight: 600,
              margin: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {language === 'fr' ? 'Conseiller IA' : 'AI Advisor'}
            </h1>
            {!isMobile && (
              <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', margin: 0 }}>
                {language === 'fr' ? 'Analyse et conseils personnalisés' : 'Personalized analysis and advice'}
              </p>
            )}
          </div>
        </div>

        {/* Messages */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: isMobile ? '16px' : '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? '12px' : '16px',
          }}
        >
          {messages.map(message => (
            <div
              key={message.id}
              style={{
                display: 'flex',
                justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
              }}
            >
              <SafeHTML
                profile="chat"
                html={formatContent(message.content)}
                style={{
                  maxWidth: isMobile ? '90%' : '75%',
                  padding: isMobile ? '12px 14px' : '16px 20px',
                  borderRadius: message.role === 'user'
                    ? '20px 20px 4px 20px'
                    : '20px 20px 20px 4px',
                  background: message.role === 'user'
                    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                    : 'var(--hover-bg)',
                  color: message.role === 'user' ? 'white' : 'var(--text-primary)',
                  fontSize: isMobile ? '14px' : '15px',
                  lineHeight: '1.6',
                }}
              />
            </div>
          ))}

          {/* Suggested Questions */}
          {messages.length === 1 && (
            <div style={{ marginTop: '16px' }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '12px' }}>
                {language === 'fr' ? 'Questions suggérées basées sur votre portefeuille :' : 'Suggested questions based on your portfolio:'}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {suggestedQuestions.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => sendMessage(question)}
                    style={{
                      padding: '10px 16px',
                      borderRadius: '20px',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      background: 'rgba(16, 185, 129, 0.05)',
                      color: '#10b981',
                      fontSize: '13px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = 'rgba(16, 185, 129, 0.15)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = 'rgba(16, 185, 129, 0.05)';
                    }}
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Loading */}
          {isLoading && (
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <div
                style={{
                  padding: '16px 20px',
                  borderRadius: '20px 20px 20px 4px',
                  background: 'var(--hover-bg)',
                  display: 'flex',
                  gap: '8px',
                }}
              >
                <span className="typing-dot" style={{ animationDelay: '0s' }}>•</span>
                <span className="typing-dot" style={{ animationDelay: '0.2s' }}>•</span>
                <span className="typing-dot" style={{ animationDelay: '0.4s' }}>•</span>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div
              style={{
                padding: '16px',
                borderRadius: '12px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#ef4444',
                textAlign: 'center',
              }}
            >
              {error}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div
          style={{
            padding: isMobile ? '12px 12px max(12px, env(safe-area-inset-bottom))' : '16px 24px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            gap: isMobile ? '8px' : '12px',
            alignItems: 'flex-end',
            background: 'var(--card-bg)',
          }}
        >
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={language === 'fr' ? 'Posez votre question...' : 'Ask a question...'}
            disabled={isLoading}
            rows={1}
            style={{
              flex: 1,
              padding: isMobile ? '12px 14px' : '14px 18px',
              borderRadius: isMobile ? '12px' : '14px',
              border: '1px solid var(--border)',
              background: 'var(--input-bg)',
              color: 'var(--text-primary)',
              fontSize: isMobile ? '16px' : '15px', // 16px prevents iOS zoom on focus
              resize: 'none',
              maxHeight: '120px',
              outline: 'none',
              fontFamily: 'inherit',
              WebkitAppearance: 'none', // Remove iOS default styling
            }}
            onInput={e => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = 'auto';
              target.style.height = Math.min(target.scrollHeight, 120) + 'px';
            }}
          />
          <button
            onClick={() => sendMessage()}
            disabled={!inputValue.trim() || isLoading}
            style={{
              width: isMobile ? '44px' : '52px',
              height: isMobile ? '44px' : '52px',
              borderRadius: isMobile ? '12px' : '14px',
              border: 'none',
              background: inputValue.trim() && !isLoading
                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                : 'var(--hover-bg)',
              cursor: inputValue.trim() && !isLoading ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
              flexShrink: 0,
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <Icons.Send
              size={isMobile ? 20 : 22}
              color={inputValue.trim() && !isLoading ? 'white' : 'var(--text-secondary)'}
            />
          </button>
        </div>
      </div>

      {/* Typing animation styles */}
      <style>{`
        @keyframes typingBounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-4px); opacity: 1; }
        }
        .typing-dot {
          display: inline-block;
          color: var(--text-secondary);
          font-size: 20px;
          animation: typingBounce 1.4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
